import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, Flex, theme, List, Grid, Alert } from 'antd';
import { WhatsAppOutlined, CheckOutlined } from '@ant-design/icons';
import { WIDGET_DATA } from '../../../constants';
import { assistantService } from '../../../../assistant-management/services/assistantManagementService';
import { useAssistant } from '../../../../../contexts/AssistantContext';
import { WhatsAppConfigValues } from '../../../types';

const { Text, Paragraph } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

// Tipos
interface WhatsAppConfigProps {
  chatbotId: string;
  chatbotData: any;
}

// Componente para el encabezado de WhatsApp
const WhatsAppHeader: React.FC<{ 
  item: typeof WIDGET_DATA[1]; 
  isMobile: boolean; 
  token: any 
}> = ({ item, isMobile, token }) => (
  <List.Item>
    <Flex
      justify="space-between"
      align={isMobile ? 'flex-start' : 'center'}
      style={{ width: '100%' }}
      vertical={isMobile}
      gap={isMobile ? token.marginSM : 0}
    >
      <Flex
        align="start"
        gap={token.marginMD}
        style={{ width: isMobile ? '100%' : 'auto' }}
      >
        {item.iconType === 'whatsapp' && <WhatsAppOutlined style={item.iconStyle} />}
        <Flex vertical gap={'8px'}>
          <Text strong>{item.title}</Text>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            {item.description}
          </Paragraph>
          <Text type="secondary">Setup: {item.setup}</Text>
        </Flex>
      </Flex>
      {/* <Button style={{ width: isMobile ? '100%' : 'auto' }}>
        {item.action}
      </Button> */}
    </Flex>
  </List.Item>
);

// Componente para el formulario de configuración
const WhatsAppConfigForm: React.FC<{
  form: any;
  onFinish: (values: WhatsAppConfigValues) => Promise<void>;
  loading: boolean;
  initialValues: Partial<WhatsAppConfigValues>;
}> = ({ form, onFinish, loading, initialValues }) => (
  <Form
    form={form}
    layout="vertical"
    onFinish={onFinish}
    autoComplete="off"
    initialValues={initialValues}
    style={{ maxWidth: '900px', width: '70%', margin: '0 auto' }}
  >
    {/*
      Off-screen decoys: browsers fill the first username/password fields they find.
      Positioning off-screen (instead of display:none) is necessary because Chromium's
      autofill heuristics skip truly-hidden inputs and target the first visible ones.
      data-1p-ignore / data-bwignore tell 1Password and Bitwarden to skip these.
    */}
    <input
      type="text"
      name="username"
      autoComplete="username"
      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      aria-hidden="true"
      tabIndex={-1}
      data-1p-ignore="true"
      data-bwignore="true"
    />
    <input
      type="password"
      name="password"
      autoComplete="current-password"
      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      aria-hidden="true"
      tabIndex={-1}
      data-1p-ignore="true"
      data-bwignore="true"
    />
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="Confidential data — authorized personnel only"
      description="These credentials grant access to the WhatsApp Business API. Do not share or expose them outside this account. Values are hidden by default; use the eye icon to reveal them when needed."
    />
    <Form.Item name="phoneNumber" label="Phone Number">
      <Input.Password autoComplete="off" />
    </Form.Item>
    <Form.Item name="accessToken" label="Access Token">
      <Input.Password autoComplete="new-password" />
    </Form.Item>
    <Form.Item name="phoneNumberId" label="Phone Number ID">
      <Input.Password autoComplete="off" />
    </Form.Item>
    <Form.Item name="verifyToken" label="Verify Token">
      <Input.Password autoComplete="new-password" />
    </Form.Item>
    <Form.Item name="webhookUrl" label="Webhook URL">
      <Input.Password autoComplete="off" />
    </Form.Item>
    <Form.Item name="whatsappBusinessAccountId" label="WhatsApp Business Account ID">
      <Input.Password autoComplete="off" />
    </Form.Item>

    <Form.Item>
      <Button
        type="primary"
        icon={<CheckOutlined />}
        htmlType="submit"
        loading={loading}
      >
        Save
      </Button>
    </Form.Item>
  </Form>
);

// Componente principal
export default function MyAssistantWhatsAppConfig({ chatbotId, chatbotData }: WhatsAppConfigProps) {
  const { token } = useToken();
  const [form] = Form.useForm<WhatsAppConfigValues>();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const { refreshCurrentAssistant } = useAssistant();

  const initialValues: Partial<WhatsAppConfigValues> = chatbotData?.whatsappConfig ?? {};

  // Sync form when the assistant data refreshes (e.g. after parent refetch).
  useEffect(() => {
    form.setFieldsValue(chatbotData?.whatsappConfig ?? {});
  }, [chatbotData?.whatsappConfig, form]);

  const handleSave = async (values: WhatsAppConfigValues) => {
    // Send only fields with a value. Clearing a field in the UI is treated as
    // "keep the current value" — wiping a credential requires explicit backend
    // support that we don't expose here.
    const partialUpdate = Object.fromEntries(
      Object.entries(values).filter(([, value]) => typeof value === 'string' && value.trim() !== '')
    ) as Partial<WhatsAppConfigValues>;

    if (Object.keys(partialUpdate).length === 0) {
      return;
    }

    try {
      setLoading(true);
      await assistantService.updateWhatsAppConfig({ chatbotId, whatsappConfig: partialUpdate as WhatsAppConfigValues });
      // Refetch the assistant so the context (and this form's initialValues) reflect
      // what was just persisted. Without this the modal reopens with the stale
      // currentAssistant held in memory until a full reload.
      await refreshCurrentAssistant();
    } catch (err) {
      // Error is already handled by the service
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ maxWidth: 1200, margin: 'auto', border: 'none' }}>
      <Flex vertical gap={token.marginLG}>
        <List
          itemLayout="horizontal"
          dataSource={[WIDGET_DATA[1]]}
          renderItem={(item) => (
            <WhatsAppHeader 
              item={item} 
              isMobile={isMobile} 
              token={token} 
            />
          )}
        />
        <WhatsAppConfigForm
          form={form}
          onFinish={handleSave}
          loading={loading}
          initialValues={initialValues}
        />
      </Flex>
    </Card>
  );
}
